export function serviceModuleKey(service) {
  return String(service?.module_key || 'suspension').trim().toLowerCase() || 'suspension'
}

export function servicePricingMode(service) {
  const mode = String(service?.pricing_mode || 'fixed').trim().toLowerCase()
  return ['fixed', 'from', 'quote_required'].includes(mode) ? mode : 'fixed'
}

export function serviceVisibility(service) {
  const visibility = String(service?.visibility || 'public').trim().toLowerCase()
  return ['public', 'internal', 'archived'].includes(visibility) ? visibility : 'public'
}

export function serviceBookingType(service) {
  const type = String(service?.booking_type || 'direct_bookable').trim().toLowerCase()
  return ['direct_bookable', 'assessment_first', 'quote_only', 'internal_addon'].includes(type) ? type : 'direct_bookable'
}

export function isSuspensionService(service) {
  return serviceModuleKey(service) === 'suspension'
}

export function suspensionServices(services) {
  return (services || []).filter(isSuspensionService)
}

export function isGeneralService(service) {
  return serviceModuleKey(service) === 'general'
}

export function generalServices(services) {
  return (services || []).filter(isGeneralService)
}

export function isPublicBookingEligibleService(service, { generalWorkshopEnabled = false } = {}) {
  const bookingType = serviceBookingType(service)
  if (service?.active === false || serviceVisibility(service) !== 'public') return false

  if (!generalWorkshopEnabled) {
    return isSuspensionService(service) && bookingType === 'direct_bookable'
  }

  if (!['suspension', 'general'].includes(serviceModuleKey(service))) return false
  return ['direct_bookable', 'assessment_first', 'quote_only'].includes(bookingType)
}

export function isCurrentPublicBookableService(service) {
  return isPublicBookingEligibleService(service, { generalWorkshopEnabled: false })
}

export function serviceCategoryLabel(service) {
  const category = service?.service_category || service?.category_row || null
  if (isGeneralService(service) && category?.name) return category.name
  const label = String(service?.category_label || service?.category || '').replace(/[-_]/g, ' ').trim()
  if (!label) return isGeneralService(service) ? 'General Workshop' : 'Other Services'
  return label.replace(/\b\w/g, letter => letter.toUpperCase())
}

export function formatServiceMoney(value, { code = false } = {}) {
  const amount = Number(value || 0)
  if (code) return `GBP ${amount.toFixed(2)}`
  const fixed = amount.toFixed(2)
  return `£${fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed}`
}

export function servicePriceLabel(service, options = {}) {
  const mode = servicePricingMode(service)
  const bookingType = serviceBookingType(service)
  if (mode === 'quote_required' || bookingType === 'quote_only') return 'Quote required'
  const amount = formatServiceMoney(service?.base_price, options)
  if (mode === 'from') return `From ${amount}`
  return amount
}

export function jobServicePriceLabel(service, intake = {}, snapshot = []) {
  const saved = snapshot.find(line => String(line.id) === String(service.id))
  if (saved?.vat_source && (saved.vat_price_status === 'fixed' || (!saved.vat_price_status && saved.pricing_mode === 'fixed'))) return formatServiceMoney(saved.base_price)
  const line = intake.prices?.tax?.lines?.find(row => row.id === `service:${service.id}`)
  if (Number.isSafeInteger(line?.gross_minor) && line.gross_minor >= 0) return formatServiceMoney(line.gross_minor / 100)
  if (saved?.vat_source) return 'Price not set'
  return servicePriceLabel(service)
}

export function serviceBookingLabel(service) {
  const bookingType = serviceBookingType(service)
  if (bookingType === 'assessment_first') return 'Assessment-led'
  if (bookingType === 'quote_only') return 'Quote request'
  if (bookingType === 'internal_addon') return 'Internal add-on'
  return ''
}

export function bookingPricingSummary(services) {
  const rows = (services || []).filter(Boolean)
  const mixedVatModules = rows.some(service => service.vat_booking_price) && new Set(rows.map(serviceModuleKey)).size > 1
  const hasQuoteRequired = mixedVatModules || rows.some(service =>
    servicePricingMode(service) === 'quote_required' || serviceBookingType(service) === 'quote_only'
  )
  const hasFromPrice = rows.some(service => servicePricingMode(service) === 'from')
  const knownTotal = rows
    .filter(service => servicePricingMode(service) !== 'quote_required' && serviceBookingType(service) !== 'quote_only')
    .reduce((sum, service) => sum + Number(service?.base_price || 0), 0)
  const pricingMode = hasQuoteRequired ? 'quote_required' : hasFromPrice ? 'from' : 'fixed'
  const estimatedTotal = hasQuoteRequired ? null : rows.reduce((sum, service) => sum + Number(service?.base_price || 0), 0)

  if (!rows.length) {
    return {
      pricing_mode: 'fixed',
      estimated_total: null,
      known_total: 0,
      summary_label: '',
      total_label: '',
      has_quote_required: false,
      has_from_price: false,
    }
  }

  if (hasQuoteRequired) {
    const knownLabel = knownTotal > 0 ? `; known prices from ${formatServiceMoney(knownTotal)}` : ''
    return {
      pricing_mode: 'quote_required',
      estimated_total: null,
      known_total: knownTotal,
      summary_label: `Quote required${knownLabel}`,
      total_label: 'Quote required',
      has_quote_required: true,
      has_from_price: hasFromPrice,
    }
  }

  if (hasFromPrice) {
    return {
      pricing_mode: 'from',
      estimated_total: estimatedTotal,
      known_total: knownTotal,
      summary_label: `Estimated from ${formatServiceMoney(estimatedTotal)}`,
      total_label: `From ${formatServiceMoney(estimatedTotal)}`,
      has_quote_required: false,
      has_from_price: true,
    }
  }

  return {
    pricing_mode: 'fixed',
    estimated_total: estimatedTotal,
    known_total: estimatedTotal,
    summary_label: estimatedTotal > 0 ? `Estimated total ${formatServiceMoney(estimatedTotal)}` : '',
    total_label: estimatedTotal > 0 ? formatServiceMoney(estimatedTotal) : '',
    has_quote_required: false,
    has_from_price: false,
  }
}

export function generalJobPricingSummary(services, { additionalWorkTotal = 0 } = {}) {
  const rows = (services || []).filter(Boolean)
  const serviceTotal = rows.reduce((sum, service) => {
    const amount = Number(service?.base_price || 0)
    return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0)
  }, 0)
  const additionalTotal = Number(additionalWorkTotal || 0)
  const billableAdditional = Number.isFinite(additionalTotal) && additionalTotal > 0 ? additionalTotal : 0
  const total = serviceTotal + billableAdditional
  const hasQuoteRequired = rows.some(service =>
    servicePricingMode(service) === 'quote_required' || serviceBookingType(service) === 'quote_only'
  )
  const hasFromPrice = rows.some(service => servicePricingMode(service) === 'from')
  const pricingMode = hasQuoteRequired ? 'quote_required' : hasFromPrice ? 'from' : 'fixed'
  const priceStatus = hasQuoteRequired ? 'quote_required' : hasFromPrice ? 'estimate' : 'final'
  const label = total > 0
    ? `${priceStatus === 'final' ? 'Total' : 'Estimated total'} ${formatServiceMoney(total)}`
    : priceStatus === 'quote_required'
      ? 'Quote required'
      : priceStatus === 'estimate'
        ? 'Estimate pending'
        : 'No price recorded'

  return {
    pricing_mode: pricingMode,
    price_status: priceStatus,
    service_total: serviceTotal,
    additional_work_total: billableAdditional,
    amount: total,
    summary_label: label,
    total_label: label,
    has_quote_required: hasQuoteRequired,
    has_from_price: hasFromPrice,
  }
}

export function normaliseBookingServiceSnapshot(value) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function bookingRequestPricingSummary(request = {}) {
  const snapshot = normaliseBookingServiceSnapshot(request.service_snapshot)
  if (snapshot.length) return bookingPricingSummary(snapshot)

  const mode = ['fixed', 'from', 'quote_required'].includes(String(request.pricing_mode || '').toLowerCase())
    ? String(request.pricing_mode).toLowerCase()
    : 'fixed'
  const total = Number(request.estimated_total || 0)
  if (mode === 'quote_required') {
    return {
      pricing_mode: 'quote_required',
      estimated_total: null,
      known_total: 0,
      summary_label: 'Quote required',
      total_label: 'Quote required',
      has_quote_required: true,
      has_from_price: false,
    }
  }
  if (mode === 'from') {
    return {
      pricing_mode: 'from',
      estimated_total: total || null,
      known_total: total,
      summary_label: total > 0 ? `Estimated from ${formatServiceMoney(total)}` : 'Estimated from price',
      total_label: total > 0 ? `From ${formatServiceMoney(total)}` : 'From price',
      has_quote_required: false,
      has_from_price: true,
    }
  }
  return {
    pricing_mode: 'fixed',
    estimated_total: total || null,
    known_total: total,
    summary_label: total > 0 ? `Estimated total ${formatServiceMoney(total)}` : '',
    total_label: total > 0 ? formatServiceMoney(total) : '',
    has_quote_required: false,
    has_from_price: false,
  }
}

export const GENERAL_CHECKLIST_PILOT_SERVICE_NAMES = [
  'Interim Service',
  'Full Service',
  'e-Bike Interim Service',
  'e-Bike Full Service',
]

const GENERAL_IMPORT_CATEGORY_LABELS = {
  brake: 'Brake Services',
  combined: 'Whole-Bike Services',
  other: 'General Workshop',
}

const SUSPENSION_ONLY_IMPORT_CATEGORIES = new Set(['fork', 'shock', 'dropper'])
const GENERAL_DROPPER_INSTALL_TERMS = /\b(installs?|installations?|fit|fits|fitting|cables?|routing|internal|internally|routed|builds?|protection|ridewrap|invisiframe)\b/i
const GENERAL_DROPPER_STRONG_INSTALL_TERMS = /\b(installs?|installations?|fit|fits|fitting|builds?)\b/i
const SUSPENSION_DROPPER_SERVICE_TERMS = /\b(service|servicing|rebuilds?|overhauls?|repairs?|lower\s+service|seal\s+service)\b/i
const SUSPENSION_SPECIFIC_SERVICE_TERMS = /\b(forks?|shocks?|lower[-\s]+legs?|air[-\s]+cans?|dampers?|reverb)\b/i
const GENERAL_CATEGORY_LOW_VALUE_WORDS = new Set([
  'and',
  'bike',
  'bikes',
  'bicycle',
  'bicycles',
  'general',
  'maintenance',
  'repair',
  'repairs',
  'service',
  'services',
  'work',
  'workshop',
])

const GENERAL_CATEGORY_SYNONYM_GROUPS = [
  {
    key: 'brakes',
    patterns: [/\bbrakes?\b/, /\bbleeds?\b/],
  },
  {
    key: 'drivetrain',
    patterns: [/\bdrivetrains?\b/, /\bgears?\b/, /\btransmissions?\b/],
  },
  {
    key: 'bearings_chassis',
    patterns: [/\bbearings?\b/, /\bbottom\s+brackets?\b/, /\bheadsets?\b/, /\bhubs?\b/, /\bpivots?\b/, /\bchassis\b/],
  },
  {
    key: 'wheels_tubeless',
    patterns: [/\bwheels?\b/, /\btubeless\b/, /\btyres?\b/, /\btires?\b/],
  },
  {
    key: 'ebike',
    patterns: [/\bebike\b/, /\be\s+bike\b/, /\belectric\b/, /\bmotors?\b/, /\bdiagnostics?\b/],
  },
  {
    key: 'build_install',
    patterns: [/\bbuilds?\b/, /\binstallations?\b/, /\binstalls?\b/, /\bprotection\b/, /\bridewrap\b/, /\binvisiframe\b/, /\bdroppers?\b/, /\bcables?\b/],
  },
]

function cleanImportText(value, max = 500) {
  return String(value || '').trim().replace(/\s+/g, ' ').slice(0, max)
}

function slugifyImportKey(value) {
  return cleanImportText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function normaliseServiceImportKey(value) {
  return cleanImportText(value).toLowerCase()
}

function normaliseGeneralCategoryMatchText(value) {
  return cleanImportText(value)
    .toLowerCase()
    .replace(/\be[-\s]?bike\b/g, 'ebike')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function singularImportToken(token) {
  if (token === 'tyres') return 'tyre'
  if (token === 'tires') return 'tire'
  if (token === 'brakes') return 'brake'
  if (token === 'gears') return 'gear'
  if (token === 'bearings') return 'bearing'
  if (token === 'wheels') return 'wheel'
  if (token.endsWith('ies') && token.length > 4) return `${token.slice(0, -3)}y`
  if (token.endsWith('s') && token.length > 4 && !token.endsWith('ss')) return token.slice(0, -1)
  return token
}

function generalCategoryTokens(value) {
  return normaliseGeneralCategoryMatchText(value)
    .split(/\s+/)
    .map(singularImportToken)
    .filter(token => token && !GENERAL_CATEGORY_LOW_VALUE_WORDS.has(token))
}

function generalCategoryTokenKey(value) {
  return [...new Set(generalCategoryTokens(value))].sort().join(' ')
}

function generalCategoryGroups(value) {
  const text = normaliseGeneralCategoryMatchText(value)
  return new Set(
    GENERAL_CATEGORY_SYNONYM_GROUPS
      .filter(group => group.patterns.some(pattern => pattern.test(text)))
      .map(group => group.key)
  )
}

function generalCategoryGroupScore(sourceGroups, candidateGroups) {
  return [...sourceGroups].filter(group => candidateGroups.has(group)).length
}

function isWeakGeneralImportCategory(row = {}) {
  const raw = normaliseGeneralCategoryMatchText(row.category || row.category_name || row.category_key)
  if (!raw || ['other', 'misc', 'miscellaneous', 'general', 'general workshop', 'workshop'].includes(raw)) return true
  return generalCategoryTokens(raw).length === 0
}

function isGeneralServicesCategory(category = {}) {
  const text = normaliseGeneralCategoryMatchText(`${category.name || ''} ${category.key || ''}`)
  return /\bgeneral\b/.test(text) && /\bservices?\b/.test(text)
}

function findGeneralImportHeadingCategory(row = {}, existingCategories = [], sourceText = '') {
  if (!sourceText || !row.name) return null

  const lines = String(sourceText).split(/\r?\n/).map((line, index) => ({
    index,
    text: normaliseGeneralCategoryMatchText(line),
  })).filter(line => line.text)
  const headings = []

  lines.forEach(line => {
    existingCategories.forEach(category => {
      const categoryName = normaliseGeneralCategoryMatchText(category?.name)
      const categoryKey = normaliseGeneralCategoryMatchText(category?.key)
      if (!categoryName && !categoryKey) return
      const categoryMatches = line.text === categoryName
        || line.text === categoryKey
        || line.text === `${categoryName} services`
        || line.text === `${categoryKey} services`
      if (categoryMatches) headings.push({ lineIndex: line.index, category })
    })
  })

  if (!headings.length) return null

  const serviceName = normaliseGeneralCategoryMatchText(row.name)
  const serviceTokens = generalCategoryTokens(row.name)
  const serviceLine = lines.find(line => {
    if (serviceName && line.text.includes(serviceName)) return true
    if (serviceTokens.length < 2) return false
    const lineTokens = new Set(generalCategoryTokens(line.text))
    return serviceTokens.filter(token => lineTokens.has(token)).length >= Math.min(3, serviceTokens.length)
  })

  if (!serviceLine) return null

  return headings
    .filter(heading => heading.lineIndex < serviceLine.index)
    .sort((a, b) => b.lineIndex - a.lineIndex)[0]?.category || null
}

function generalCategoryMatchScore(row = {}, category = {}) {
  const parsedCategoryText = `${row.category_name || ''} ${row.category_key || ''} ${row.category || ''}`
  const sourceText = `${parsedCategoryText} ${row.name || ''}`
  const candidateText = `${category.name || ''} ${category.key || ''}`
  const parsedKey = generalCategoryTokenKey(parsedCategoryText)
  const candidateKey = generalCategoryTokenKey(candidateText)

  if (parsedKey && candidateKey && parsedKey === candidateKey) {
    return { score: 100, matchType: 'exact' }
  }

  const sourceGroups = generalCategoryGroups(sourceText)
  const candidateGroups = generalCategoryGroups(candidateText)
  const groupScore = generalCategoryGroupScore(sourceGroups, candidateGroups)
  if (groupScore > 0) {
    return { score: 72 + (groupScore * 12), matchType: 'matched' }
  }

  const sourceTokens = new Set(generalCategoryTokens(parsedCategoryText))
  const candidateTokens = new Set(generalCategoryTokens(candidateText))
  const tokenOverlap = [...sourceTokens].filter(token => candidateTokens.has(token)).length
  if (tokenOverlap > 0) {
    return { score: 36 + (tokenOverlap * 10), matchType: 'matched' }
  }

  if (isWeakGeneralImportCategory(row) && isGeneralServicesCategory(category)) {
    return { score: 58, matchType: 'matched' }
  }

  return { score: 0, matchType: 'created' }
}

export function isPilotGeneralChecklistServiceName(name) {
  const normalised = normaliseServiceImportKey(name)
  return GENERAL_CHECKLIST_PILOT_SERVICE_NAMES
    .some(pilotName => normaliseServiceImportKey(pilotName) === normalised)
}

function normaliseGeneralImportPricingMode(service = {}, combinedText = '') {
  const rawMode = cleanImportText(service.pricing_mode).toLowerCase()
  if (['fixed', 'from', 'quote_required'].includes(rawMode)) return rawMode
  if (/\b(quote|quote required|poa|price on application|estimate required|tbc)\b/i.test(combinedText)) return 'quote_required'
  if (/\b(from|starting at|starts at|from £|from gbp)\b/i.test(combinedText)) return 'from'
  return 'fixed'
}

function normaliseGeneralImportPrice(service = {}) {
  const direct = Number(service.base_price ?? service.price ?? service.amount)
  if (Number.isFinite(direct)) return Math.max(0, direct)
  const match = cleanImportText(service.base_price ?? service.price ?? service.amount)
    .replace(/,/g, '')
    .match(/\d+(\.\d+)?/)
  return match ? Math.max(0, Number(match[0])) : 0
}

function normaliseGeneralImportLabour(service = {}) {
  const direct = Number(service.expected_labour_minutes ?? service.labour_minutes ?? service.duration_minutes)
  if (Number.isFinite(direct)) return Math.max(0, Math.round(direct))
  const duration = cleanImportText(service.duration ?? service.expected_labour_time ?? service.labour_time)
  const hours = duration.match(/(\d+(\.\d+)?)\s*(h|hr|hour|hours)\b/i)
  if (hours) return Math.max(0, Math.round(Number(hours[1]) * 60))
  const minutes = duration.match(/(\d+)\s*(m|min|mins|minute|minutes)\b/i)
  return minutes ? Math.max(0, Math.round(Number(minutes[1]))) : 0
}

function generalImportCategoryName(service = {}) {
  const explicit = cleanImportText(service.category_name || service.category_label)
  if (explicit) return explicit
  const category = cleanImportText(service.category).toLowerCase()
  if (GENERAL_IMPORT_CATEGORY_LABELS[category]) return GENERAL_IMPORT_CATEGORY_LABELS[category]
  if (category) return category.replace(/[-_]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
  return 'General Workshop'
}

function looksGeneralDropperInstallImport(rawCategory = '', name = '', description = '') {
  const text = `${rawCategory} ${name} ${description}`
  const mentionsDropper = /\b(droppers?|seat\s*posts?|seatposts?)\b/i.test(text)
  const serviceLike = SUSPENSION_DROPPER_SERVICE_TERMS.test(text)
  const strongInstall = GENERAL_DROPPER_STRONG_INSTALL_TERMS.test(text)
  return mentionsDropper && GENERAL_DROPPER_INSTALL_TERMS.test(text) && (!serviceLike || strongInstall)
}

function looksSuspensionOnlyGeneralImport(rawCategory = '', name = '', description = '') {
  const text = `${rawCategory} ${name} ${description}`
  const dropperOrSeatpost = /\b(droppers?|seat\s*posts?|seatposts?)\b/i.test(text)
  const dropperInstall = looksGeneralDropperInstallImport(rawCategory, name, description)
  const dropperService = dropperOrSeatpost && SUSPENSION_DROPPER_SERVICE_TERMS.test(text)
  const dropperCategory = rawCategory === 'dropper'

  return rawCategory === 'fork'
    || rawCategory === 'shock'
    || SUSPENSION_SPECIFIC_SERVICE_TERMS.test(text)
    || (dropperOrSeatpost && !dropperInstall && (dropperCategory || dropperService))
}

export function normaliseGeneralServiceImportPreview(service = {}, index = 0) {
  const name = cleanImportText(service.name, 120)
  const description = cleanImportText(service.description || service.customer_description || service.notes, 500)
  const combinedText = `${name} ${description} ${cleanImportText(service.price)} ${cleanImportText(service.pricing_mode)}`
  const rawCategory = cleanImportText(service.category).toLowerCase()
  const pricingMode = normaliseGeneralImportPricingMode(service, combinedText)
  const basePrice = normaliseGeneralImportPrice(service)
  const categoryName = generalImportCategoryName(service)
  const categoryKey = slugifyImportKey(categoryName) || 'general-workshop'
  const reviewReasons = []
  const pilotMatch = isPilotGeneralChecklistServiceName(name)
  const suspensionOnly = !pilotMatch && (
    (SUSPENSION_ONLY_IMPORT_CATEGORIES.has(rawCategory) && rawCategory !== 'dropper')
    || looksSuspensionOnlyGeneralImport(rawCategory, name, description)
  )

  if (!name) reviewReasons.push('Missing service name')
  if (!rawCategory || rawCategory === 'other') reviewReasons.push('Category may need review')
  if (basePrice === 0 && pricingMode !== 'quote_required') reviewReasons.push('Price may need review')

  return {
    source_index: index,
    category_name: categoryName,
    category_key: categoryKey,
    raw_category_name: categoryName,
    raw_category_key: categoryKey,
    name,
    description,
    pricing_mode: pricingMode,
    base_price: basePrice,
    visibility: 'internal',
    booking_type: pricingMode === 'quote_required' ? 'quote_only' : 'assessment_first',
    expected_labour_minutes: normaliseGeneralImportLabour(service),
    internal_notes: '',
    needs_review: reviewReasons.length > 0,
    review_reasons: reviewReasons,
    is_pilot_checklist_service: pilotMatch,
    include: !suspensionOnly && Boolean(name),
    skip: suspensionOnly || !name,
    skip_reason: suspensionOnly
      ? 'Looks suspension-specific; keep it under Suspension Services.'
      : (!name ? 'Missing service name.' : ''),
  }
}

export function generalServiceImportDuplicateKey(service = {}, categoryKey = '') {
  return [
    normaliseServiceImportKey(categoryKey || service.category_key || service.category || 'general-workshop'),
    normaliseServiceImportKey(service.name),
  ].join('::')
}

export function matchGeneralImportCategory(row = {}, existingCategories = [], options = {}) {
  const activeCategories = (existingCategories || []).filter(category => category && category.active !== false)
  const fallbackName = row.category_name || generalImportCategoryName(row)
  const fallbackKey = row.category_key || slugifyImportKey(fallbackName) || 'general-workshop'
  const weak = isWeakGeneralImportCategory(row)
  const headingCategory = findGeneralImportHeadingCategory(row, activeCategories, options.sourceText)
  let best = null

  if (headingCategory) {
    return {
      category: headingCategory,
      category_key: headingCategory.key,
      category_name: headingCategory.name,
      match_type: 'exact',
      match_score: 120,
      needs_review: false,
    }
  }

  activeCategories.forEach(category => {
    const result = generalCategoryMatchScore(row, category)
    if (!best || result.score > best.score) {
      best = { category, ...result }
    }
  })

  if (best && best.score >= 50) {
    return {
      category: best.category,
      category_key: best.category.key,
      category_name: best.category.name,
      match_type: best.matchType,
      match_score: best.score,
      needs_review: weak && !isGeneralServicesCategory(best.category),
    }
  }

  return {
    category: null,
    category_key: fallbackKey,
    category_name: fallbackName,
    match_type: 'created',
    match_score: 0,
    needs_review: weak,
  }
}

export function applyGeneralImportCategoryMatch(row = {}, existingCategories = [], options = {}) {
  const match = matchGeneralImportCategory(row, existingCategories, options)
  const reviewReasons = [...(row.review_reasons || [])]
  if (match.needs_review && !reviewReasons.includes('Category match needs review')) {
    reviewReasons.push('Category match needs review')
  }

  return {
    ...row,
    category_key: match.category_key,
    category_name: match.category_name,
    matched_category_id: match.category?.id || null,
    category_match_type: match.match_type,
    category_match_score: match.match_score,
    category_needs_review: match.needs_review,
    category_selection: match.category?.id || '__new__',
    custom_category_name: match.category?.id ? '' : match.category_name,
    include: row.include !== false && row.skip !== true,
    needs_review: row.needs_review || match.needs_review || reviewReasons.length > 0,
    review_reasons: reviewReasons,
  }
}

export function buildGeneralServiceImportPreviewRows(services = [], existingCategories = [], options = {}) {
  return (services || [])
    .map((service, index) => applyGeneralImportCategoryMatch(
      normaliseGeneralServiceImportPreview(service, index),
      existingCategories,
      options
    ))
    .filter(service => service.name || service.skip)
}

export function generalServiceImportRowSkipReason(row = {}) {
  if (row.skip) return row.skip_reason || 'Skipped.'
  if (row.include === false) return 'Skipped by user.'
  if (!cleanImportText(row.name)) return 'Missing service name.'
  return ''
}

export function isGeneralServiceImportRowIncluded(row = {}) {
  return !generalServiceImportRowSkipReason(row)
}

export function summariseGeneralChecklistAssignmentResults(results = []) {
  return (results || []).reduce((summary, row = {}) => {
    const status = normaliseServiceImportKey(row.status)
    if (status === 'created') summary.created += 1
    else if (status === 'reactivated') summary.reactivated += 1
    else if (status === 'already_present') summary.alreadyPresent += 1
    else {
      summary.failed += 1
      summary.failures.push({
        serviceName: cleanImportText(row.service_name || row.serviceName || 'Pilot service'),
        message: cleanImportText(row.error || row.message || 'Checklist assignment needs manual review.'),
      })
    }
    return summary
  }, {
    created: 0,
    reactivated: 0,
    alreadyPresent: 0,
    failed: 0,
    failures: [],
  })
}
